import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Helmet } from "react-helmet-async";
const DataIntegration = () => {
  const phrases = [
    "Enterprise Data Pipelines",
    "Data Integration"
  ];

  const cardData = [
    { type: 'purple-clay', content: '🔄' },
    { type: 'smooth-wave', content: '🗄️' },
    { type: 'abstract-blocks', content: '🔗' },
    { type: 'pink-sphere', content: '⚡' }
  ];

  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [cardIndex, setCardIndex] = useState(0);

  useEffect(() => {
    const currentPhrase = phrases[currentPhraseIndex];
    let timer;

    const handleTyping = () => {
      if (!isDeleting) {
        setDisplayedText(currentPhrase.substring(0, displayedText.length + 1));
        if (displayedText === currentPhrase) {
          timer = setTimeout(() => setIsDeleting(true), 1600);
          return;
        }
      } else {
        setDisplayedText(currentPhrase.substring(0, displayedText.length - 1));
        if (displayedText === '') {
          setIsDeleting(false);
          setCurrentPhraseIndex((prev) => (prev + 1) % phrases.length);
          return;
        }
      }

      const speed = isDeleting ? 30 : 80;
      timer = setTimeout(handleTyping, speed);
    };

    timer = setTimeout(handleTyping, 100);
    return () => clearTimeout(timer);
  }, [displayedText, isDeleting, currentPhraseIndex]);

  useEffect(() => {
    const cardTimer = setInterval(() => {
      setCardIndex((prev) => (prev + 1) % cardData.length);
    }, 1100);
    return () => clearInterval(cardTimer);
  }, []);

  // Intersection Observer for Scroll Animations
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.15
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-visible');
        }
      });
    }, observerOptions);

    const animatedElements = document.querySelectorAll(
      '.scroll-anim-fade-in, .scroll-anim-fade-in-up, .scroll-anim-fade-in-left, .scroll-anim-fade-in-right, .scroll-anim-flip, .scroll-anim-zoom-in'
    );

    animatedElements.forEach((el) => observer.observe(el));

    return () => {
      animatedElements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  // Content Data
  const coreServices = [
    { icon: "🔄", title: "ETL & ELT Pipeline Engineering", desc: "Design high-throughput Extract, Transform, and Load engines to centralize unstructured data into structured assets." },
    { icon: "⚡", title: "Real-Time Event Streaming", desc: "Build low-latency messaging frameworks to ingest and route continuous data events instantly across the organization." },
    { icon: "🔗", title: "Enterprise API Architecture", desc: "Construct bidirectional, authenticated REST and GraphQL connection points to tie independent operational modules together." },
    { icon: "🗄️", title: "Data Warehouse & Lakehouse Hubs", desc: "Structure analytics-ready repositories combining flexible lake architecture with absolute cloud warehouse performance." },
    { icon: "🧼", title: "Automated Data Cleansing & Quality", desc: "Deploy dynamic normalization engines to continuously isolate anomalies, repair syntax errors, and scrub duplicates." },
    { icon: "🧬", title: "Master Data Management (MDM)", desc: "Establish unified, immutable baseline profiles for core company entities to eliminate cross-departmental record conflicts." },
    { icon: "🔐", title: "Governance & Data Masking Layers", desc: "Inject strict role-based encryption boundaries, catalog tags, and tracking layers to maintain airtight compliance." }
  ];

  const advancedServices = [
    { icon: "🤖", title: "AI-Driven Intelligent Mapping", desc: "Deploy smart transformation heuristics to automate the translation of mismatched legacy schemas into uniform structures." },
    { icon: "🏥", title: "Legacy System Synchronization", desc: "Bridge decades-old local servers safely with public cloud data layers using micro-batch webhooks and change-data-capture (CDC)." },
    { icon: "📡", title: "Multi-Cloud Mesh Architecture", desc: "Design cross-platform distributed routing fabrics to allow transparent querying across distinct provider networks." },
    { icon: "🛡️", title: "Proactive Data Lineage Auditing", desc: "Map complete visual asset histories from raw initial ingestion origins down to finished downstream dashboard reporting nodes." },
    { icon: "📈", title: "High-Volume Batch Processing", desc: "Configure optimized parallel computation systems to efficiently process massive daily corporate analytical archives." },
    { icon: "🧪", title: "Automated Pipeline Testing", desc: "Construct end-to-end continuous orchestration suites to instantly catch missing column boundaries or corrupted fields." },
    { icon: "💼", title: "SaaS Application Harmonization", desc: "Unify external functional modules smoothly with internal records, eliminating isolated system silos completely." },
    { icon: "🏗️", title: "Metadata Catalog Orchestration", desc: "Deploy search-optimized enterprise directories to ensure rapid asset discovery for data engineering units." },
    { icon: "🛣️", title: "Reverse ETL Operational Routing", desc: "Push warehouse insights back out to front-line customer success, marketing, and sales systems dynamically." }
  ];

  const technologies = {
    "Ingestion Engines": ["Apache Kafka / Confluent", "Apache NiFi / Airflow", "Talend Open Studio", "AWS Glue / Azure Data Factory"],
    "Storage & Analytics": ["Snowflake / Databricks", "Google BigQuery", "Amazon Redshift", "Apache Spark Streaming"],
    "Modern Transform Layers": ["dbt (Data Build Tool)", "Logstash / Fluentd", "Python (Pandas / PySpark)", "Apache Flink"],
    "Database Systems": ["PostgreSQL / MySQL Core", "MongoDB / Cassandra", "Neo4j Graph Database", "Redis In-Memory Tier"],
    "Governance & Security": ["Apache Atlas Catalog", "Collibra Governance", "HashiCorp Vault Secrets", "AWS IAM Identity Policies"]
  };

  const processSteps = [
    { num: "01", title: "Ecosystem Discovery", desc: "Auditing current functional storage layouts, identifying schema friction, and mapping target asset paths." },
    { num: "02", title: "Schema Blueprinting", desc: "Designing unified data models, transaction keys, integration protocols, and staging zones." },
    { num: "03", title: "Pipeline Development", desc: "Writing clean parsing logic, low-latency connector rules, and robust server transformation scripts." },
    { num: "04", title: "Data Synthesis", desc: "Binding event brokers to production endpoints and establishing clean bidirectional transfer flows." },
    { num: "05", title: "Quality Verification", desc: "Running edge-case volume tests, boundary validations, and compliance data masking patterns." },
    { num: "06", title: "Production Deployment", desc: "Executing parallel live processing tracks and performing safe final DNS sync switchovers." },
    { num: "07", title: "Continuous Refinement", desc: "Monitoring stream velocity metrics, optimizing heavy query execution paths, and scaling auto-compute arrays." }
  ];

  const industries = [
    "Global Financial Clearing", "Healthcare & EMR Systems", "Telecommunications Grid", "E-Commerce Supply Logistics",
    "Energy Grid Infrastructure", "High-Tech & Enterprise SaaS", "Automotive Fleet Telematics", "Government Operations",
    "Insurance Risk Analysis", "Global Supply Chain Webs", "Digital Media Networks", "Aerospace Data Routing"
  ];

  const designBenefits = [
    "Single Source of Truth Realized", "Sub-Second Event Ingestion Velocity", "Airtight Internal Asset Governance",
    "Highly Scalable Cloud Data Hubs", "Eliminated Departmental Silos", "Clean, Analytics-Ready Repositories",
    "Automated Quality Enforcement", "Drastically Reduced Report Latency"
  ];

  const whyChooseUs = [
    "Certified Data Architects", "Robust ETL/ELT Production Experience", "Modern Event-Driven Architecture Prowess",
    "Flawless Complex Schema Mapping", "Deep Understanding of Regulatory Compliance", "High-Performance Clean Query Code",
    "Strategic Zero-Downtime Pipeline Cutovers", "Transparent Agile Delivery Sprint Cadences", "Proven Enterprise Database Clean-Ups",
    "Dedicated Post-Go-Live Maintenance Support", "Native Automation-First Philosophy", "100% Platform Security Architecture Integrity"
  ];

  return (

    <>
      <Helmet>
        {/* Primary SEO */}
        <title>
          Data Integration Services | Enterprise Data Integration Solutions | Affsoft IT Solutions Pvt. Ltd.
        </title>

        <meta
          name="title"
          content="Data Integration Services | Enterprise Data Integration Solutions | Affsoft IT Solutions Pvt. Ltd."
        />

        <meta
          name="description"
          content="Affsoft IT Solutions Pvt. Ltd. provides secure and scalable data integration services, including ETL development, API integration, cloud data integration, database integration, data migration, real-time data synchronization, and enterprise data management solutions."
        />

        <meta
          name="keywords"
          content="Data Integration, Data Integration Services, Enterprise Data Integration, ETL Development, API Integration, Data Migration, Cloud Data Integration, Database Integration, Data Synchronization, Data Warehousing, Business Intelligence, Data Management, System Integration, Real-Time Data Integration, Enterprise Data Solutions, Data Engineering, Data Integration Company, Affsoft IT Solutions Pvt. Ltd."
        />

        <meta name="robots" content="index, follow" />
        <meta name="author" content="Affsoft IT Solutions Pvt. Ltd." />
        <meta name="language" content="English" />
        <meta name="theme-color" content="#0d5c53" />

        {/* Canonical */}
        <link
          rel="canonical"
          href="https://www.affsoft.com/data-integration"
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta
          property="og:site_name"
          content="Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          property="og:title"
          content="Professional Data Integration Services | Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          property="og:description"
          content="Connect your business systems with expert data integration services from Affsoft IT Solutions Pvt. Ltd. We deliver ETL solutions, API integrations, cloud data integration, real-time synchronization, and enterprise data management."
        />
        <meta
          property="og:url"
          content="https://www.affsoft.com/data-integration"
        />
        <meta
          property="og:image"
          content="https://www.affsoft.com/images/affsoft-logo.png"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Data Integration Services | Enterprise Data Integration Solutions | Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          name="twitter:description"
          content="Streamline your business data with data integration services from Affsoft IT Solutions Pvt. Ltd. We specialize in ETL, API integration, cloud data integration, database synchronization, and enterprise data solutions."
        />
        <meta
          name="twitter:image"
          content="https://www.affsoft.com/images/affsoft-logo.png"
        />
      </Helmet>
      <div className="dataintegration-services-page bg-light" style={{ fontFamily: '"Inter", sans-serif', overflowX: 'hidden' }}>

        {/* ================= HERO SECTION ================= */}
        <div
          className="hero-container container-fluid d-flex align-items-center justify-content-center position-relative overflow-hidden"
          style={{
            backgroundImage: "url('./images/home.jpg')",
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            minHeight: '85vh',
            backgroundColor: '#0d111b' // dark contextual fallback
          }}
        >
          {/* Background Grid Lines */}
          <div className="grid-overlay position-absolute top-0 start-0 w-100 h-100" style={{ opacity: 0.12 }}>
            <div className="grid-line line-v-1"></div>
            <div className="grid-line line-v-2"></div>
            <div className="grid-line line-v-3"></div>
          </div>

          {/* Infinite Slow-Scrolling Marquee Text Background Wrapper */}
          <div className="marquee-wrapper select-none position-absolute w-100 scroll-anim-fade-in" style={{ top: '15%', opacity: 0.05 }}>

          </div>

          <div className="marquee-wrapper select-none position-absolute w-100 scroll-anim-fade-in">
            <div className="marquee-content text-white">
              <span>Enterprise Data Pipelines &nbsp; and &nbsp;</span>
              <span>Data Integration </span>
            </div>
          </div>

          {/* Main Content Layout */}
          <div className="container position-relative z-3 my-5">
            <div className="row align-items-center py-4">

              {/* Left Side Info */}
              <div className="col-12 col-md-5 text-start d-flex flex-column align-items-start hero-left-pane text-white mb-4 mb-md-0 scroll-anim-fade-in-left">
                <div className="asterisk-icon mb-3 text-warning fs-3">✳</div>
                <h1 className="fw-bold mb-3 display-5">Data Integration</h1>
                <p className="tagline mb-4 text-white" style={{ fontSize: '1rem', lineHeight: '1.6', opacity: 0.9 }}>
                  At Affsoft IT Solutions Pvt. Ltd., we transform disconnected, fragmented information layers into powerful, unified corporate intelligence architectures. Our advanced integration systems deliver seamless automated pipeline orchestration, real-time streaming ingestion, and robust master data strategies to turn complex records into pure analytical leverage.
                </p>
                <button className="btn btn-outline-light d-flex align-items-center gap-2 px-4 py-2 rounded-pill fw-semibold">
                  <span>➔</span>
                  <span>About Affsoft</span>
                </button>
              </div>

              {/* Center Section */}
              <div className="col-12 col-md-4 text-center mb-4 mb-md-0 hero-center-pane scroll-anim-flip">
                <div className="typing-badge px-4 py-3 mx-auto bg-dark bg-opacity-75 rounded-pill border border-secondary shadow-lg d-inline-block">
                  <span className="typed-text text-white fw-mono fs-4">{displayedText}</span>
                  <span className="cursor text-warning fs-4 animate-blink">|</span>
                </div>
              </div>

              {/* Right Section */}
              <div className="col-12 col-md-3 d-flex justify-content-center justify-content-md-end hero-right-pane scroll-anim-fade-in-right">
                <div className="glass-card-frame shadow-lg p-3 bg-white bg-opacity-10 rounded-4 border border-light" style={{ width: '220px', backdropFilter: 'blur(10px)' }}>
                  <div className={`inner-graphic-card graphic-${cardData[cardIndex].type} d-flex align-items-center justify-content-center rounded-3 bg-dark`} style={{ height: '180px' }}>
                    <span className="graphic-icon display-3">{cardData[cardIndex].content}</span>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* ================= CORE SOLUTIONS SECTION ================= */}
        <section className="py-5 bg-white">
          <div className="container">
            <div className="text-center max-width-700 mx-auto mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-primary px-3 py-2 rounded-pill mb-2 text-uppercase">System Harmonization</span>
              <h2 className="fw-bold text-dark display-6">Clean Engineering Built for High-Volume Processing</h2>
              <p className="text-muted mt-3">
                Isolated operational databases create broken reports, conflicting numbers, and strategic delays. At Affsoft IT Solutions Pvt. Ltd., we construct secure, version-controlled pipelines that keep core systems synchronized while preserving target architecture health.
              </p>
            </div>

            <div className="row g-4">
              {coreServices.map((service, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-zoom-in" key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="card h-100 shadow-sm border-0 rounded-4 p-4 hover-translate transition-all">
                    <div className="fs-1 mb-3 text-primary">{service.icon}</div>
                    <h4 className="fw-bold fs-5 mb-2 text-dark">{service.title}</h4>
                    <p className="text-muted mb-0 small">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Integration Architecture Checklist */}
            <div className="mt-5 p-4 rounded-4 bg-light border scroll-anim-fade-in-up">
              <h5 className="fw-bold text-center text-dark mb-4">Why Modernize Your Corporate Information Ecosystem?</h5>
              <div className="row g-3">
                {designBenefits.map((benefit, index) => (
                  <div className="col-6 col-md-3 d-flex align-items-center gap-2" key={index}>
                    <span className="text-success fs-5">✓</span>
                    <span className="text-secondary small fw-medium">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ================= ADVANCED BUSINESS SYSTEMS ================= */}
        <section className="py-5 bg-light">
          <div className="container">
            <div className="text-center max-width-700 mx-auto mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-warning text-dark px-3 py-2 rounded-pill mb-2 text-uppercase fw-bold">Elite Architecture</span>
              <h2 className="fw-bold text-dark display-6">Advanced Processing Networks Programmed with Precision</h2>
              <p className="text-muted mt-3">
                We extend out-of-the-box software APIs into deep enterprise data structures. From real-time change synchronization to horizontal schema discovery tracks, our systems create optimized corporate environments designed to adapt.
              </p>
            </div>

            <div className="row g-4">
              {advancedServices.map((service, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-zoom-in" key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="card h-100 shadow-sm border-0 rounded-4 p-4 bg-white hover-translate transition-all">
                    <div className="fs-1 mb-3 text-warning">{service.icon}</div>
                    <h4 className="fw-bold fs-5 mb-2 text-dark">{service.title}</h4>
                    <p className="text-muted mb-0 small">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= TECHNOLOGIES SECTION ================= */}
        <section className="py-5 bg-dark text-white">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-warning text-dark px-3 py-2 rounded-pill mb-2 text-uppercase fw-bold">Ecosystem Tech Stack</span>
              <h2 className="fw-bold display-6">Data Frameworks & Orchestrators We Leverage</h2>
              <p className="text-light-50">We configure and maintain heavy pipeline operations using modern, resilient framework standards.</p>
            </div>

            <div className="row g-4 justify-content-center">
              {Object.entries(technologies).map(([category, list], index) => (
                <div className="col-12 col-sm-6 col-md-4 col-lg scroll-anim-fade-in" key={index}>
                  <div className="bg-secondary bg-opacity-25 p-4 rounded-4 h-100 border border-secondary border-opacity-50">
                    <h5 className="fw-bold text-warning mb-3 border-bottom border-secondary pb-2">{category}</h5>
                    <ul className="list-unstyled mb-0">
                      {list.map((tech, i) => (
                        <li key={i} className="mb-2 d-flex align-items-center gap-2 small">
                          <span className="text-warning opacity-75">•</span> {tech}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= ROADMAP PROCESS ================= */}
        <section className="py-5 bg-white">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-secondary px-3 py-2 rounded-pill mb-2 text-uppercase">Execution Lifecycles</span>
              <h2 className="fw-bold text-dark display-6">Our Engineered Integration Track</h2>
              <p className="text-muted">A clear, disciplined framework mapping isolated transaction points securely into centralized arrays.</p>
            </div>

            <div className="row g-3">
              {processSteps.map((step, index) => (
                <div className="col-12 col-md-6 col-lg scroll-anim-fade-in-up" key={index} style={{ animationDelay: `${index * 0.15}s` }}>
                  <div className="p-4 rounded-4 bg-light border-start border-warning border-4 h-100 shadow-sm">
                    <div className="fw-bold text-warning display-6 mb-2">{step.num}</div>
                    <h5 className="fw-bold text-dark fs-6">{step.title}</h5>
                    <p className="text-muted small mb-0">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= INDUSTRIES SERVED ================= */}
        <section className="py-5 bg-light">
          <div className="container text-center">
            <h2 className="fw-bold text-dark mb-4 display-6 scroll-anim-fade-in-up">Industries We Empower</h2>
            <div className="d-flex flex-wrap justify-content-center gap-2 max-width-900 mx-auto scroll-anim-zoom-in">
              {industries.map((industry, index) => (
                <span className="badge bg-white text-dark border px-3 py-2 rounded-pill shadow-sm fs-7 fw-medium" key={index}>
                  🏢 {industry}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* ================= WHY CHOOSE AFFSOFT ================= */}
        <section className="py-5 bg-white border-top">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <h2 className="fw-bold text-dark display-6">Why Partner with Affsoft IT Solutions Pvt. Ltd.?</h2>
              <p className="text-muted">We join structural validation routines with clean network models to scale enterprise analytics systems safely.</p>
            </div>

            <div className="row g-3">
              {whyChooseUs.map((reason, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-fade-in-left" key={index} style={{ animationDelay: `${(index % 3) * 0.1}s` }}>
                  <div className="d-flex align-items-center gap-3 p-3 bg-light rounded-3 h-100">
                    <span className="text-warning fs-4">🛡️</span>
                    <span className="text-dark fw-semibold small">{reason}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Custom Styles Injection */}
        <style>{`
        .hover-translate {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .hover-translate:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important;
        }
        .text-light-50 {
          color: rgba(255, 255, 255, 0.7);
        }
        .max-width-700 {
          max-width: 700px;
        }
        .max-width-900 {
          max-width: 900px;
        }
        .animate-blink {
          animation: blink 1s step-end infinite;
        }
        @keyframes blink {
          from, to { color: transparent }
          50% { color: inherit }
        }

        /* --- Scroll Animation Core --- */
        .scroll-anim-fade-in,
        .scroll-anim-fade-in-up,
        .scroll-anim-fade-in-left,
        .scroll-anim-fade-in-right,
        .scroll-anim-flip,
        .scroll-anim-zoom-in {
          opacity: 0;
          will-change: transform, opacity;
          transition: all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }

        /* Visibility Trigger via IntersectionObserver */
        .animate-visible {
          opacity: 1 !important;
          transform: none !important;
        }

        /* --- Animations Configurations --- */
        .scroll-anim-fade-in-up {
          transform: translateY(40px);
        }
        .scroll-anim-fade-in-left {
          transform: translateX(-50px);
        }
        .scroll-anim-fade-in-right {
          transform: translateX(50px);
        }
        .scroll-anim-zoom-in {
          transform: scale(0.85);
        }
        .scroll-anim-flip {
          transform: perspective(400px) rotateY(90deg);
          transition: all 1s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }
        
        .scroll-anim-flip.animate-visible {
          transform: perspective(400px) rotateY(0deg) !important;
        }
      `}</style>

      </div>

    </>
  );
};

export default DataIntegration;